import React from "react"
import Layout from "../../../components/Layout"
import PageHero from "../../../components/PageHero"
import News from "../../../components/News"
import "../../../styles/news.scss"
import "../../../styles/caseStudy.scss"
import heroImg from "../../../images/caseStudies/eme-story1.jpg"
import clientLogo from "../../../images/clients/Colored/EME-logo.png"

const CHALLENGES = [
  {
    title: "Security Gaps",
    desc: "Sensitive procurement data was accessible to all staff, exposing confidential supplier pricing and tender information across departments.",
  },
  {
    title: "Unverified Orders",
    desc: "No structured approval workflow meant purchase orders could be raised without management oversight, creating compliance and budget risks.",
  },
  {
    title: "Reporting Limitations",
    desc: "Native Odoo templates were inadequate for multi-currency operations, making it impossible to generate accurate BOQs and financial statements.",
  },
]

const SOLUTIONS = [
  { num: "01", text: "Tiered Procurement Approval", desc: "A 3-tier approval chain (Engineer → Supervisor → Management) ensures every purchase order is reviewed and authorised before commitment." },
  { num: "02", text: "Bidder Lifecycle Migration", desc: "Streamlined the entire Bidder Flow within Odoo — from vendor registration and RFQ issuance to bid comparison and award, all in one place." },
  { num: "03", text: "Standardised Reporting", desc: "Custom Bill of Quantities (BOQ) templates and multi-currency Statements of Account built directly into Odoo to meet UAE compliance needs." },
  { num: "04", text: "Fleet & Expense Integration", desc: "Vehicle maintenance schedules and petty cash workflows linked directly to the accounting module, eliminating manual reconciliation." },
]

const RESULTS = [
  { metric: "3-Stage", label: "Procurement security with department-level data silos" },
  { metric: "100%", label: "Paperless MEP transactions with full audit trail" },
  { metric: "Real-Time", label: "Multi-currency financial tracking across all projects" },
]

const RESULT_BULLETS = [
  "Complete visibility into procurement spend from requisition to payment",
  "Faster tender cycles with structured bidder comparison built into Odoo",
  "Fleet and petty cash expenses automatically reconciled with project accounts",
]

const EmeAquaticSystems = () => (
  <div className="CaseStudeies">
    <Layout
      pageTitle="Visiomate - EME Aquatic Systems Case Study"
      descrip="Procurement security, approval workflows, and multi-currency reporting for EME Aquatic Systems — a 40-year-old aquatic infrastructure leader in the UAE."
    >
      <PageHero
        eyebrow="Customer Story"
        title="EME Aquatic Systems"
        description="Procurement security and financial clarity for Abu Dhabi's leading aquatic infrastructure company"
      />
      {/* About */}
      <section className="vm-news-section vm-news-section--light">
        <div className="vm-news-inner cs-intro">
          <div className="cs-intro__img">
            <img src={heroImg} alt="EME Aquatic Systems" />
          </div>
          <div className="cs-intro__text">
            <img src={clientLogo} alt="EME Aquatic Systems logo" className="cs-client-logo" />
            <h2 className="vm-news-heading vm-news-heading--dark">
              Transforming Procurement &amp; Finance for a 40-Year UAE Institution
            </h2>
            <p className="vm-news-sub vm-news-sub--dark">
              EME Aquatic Systems (formerly Engineering Marketing Est.) is an Abu Dhabi-based
              company with over 40 years of expertise designing and delivering Olympic swimming
              pools, water parks, spas, and water filtration systems across the MENA region.
              As the exclusive Mitsubishi agent for aquatic equipment in the UAE, EME manages
              complex multi-currency procurement and project accounting — and needed a system
              that could keep up.
            </p>
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="vm-news-section vm-news-section--gray">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">What They Were Up Against</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              Before working with Visiomate, EME faced three critical operational gaps in their Odoo environment.
            </p>
          </div>
          <div className="cs-challenge-grid">
            {CHALLENGES.map(c => (
              <div key={c.title} className="cs-challenge-card">
                <div className="cs-challenge-card__icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <h3 className="cs-challenge-card__title">{c.title}</h3>
                <p className="cs-challenge-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="vm-news-section vm-news-section--light">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">What We Built</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              We extended EME's existing Odoo implementation with targeted modules and custom workflows to address each gap.
            </p>
          </div>
          <div className="cs-solutions-grid">
            {SOLUTIONS.map(s => (
              <div key={s.num} className="cs-solution-item">
                <div className="cs-solution-item__badge">{s.num}</div>
                <div>
                  <span className="cs-solution-item__text">{s.text}</span>
                  <p className="cs-solution-item__desc">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="vm-news-section vm-news-section--gray">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">The Outcome</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              EME now operates with end-to-end procurement control and real-time financial clarity.
            </p>
          </div>
          <div className="cs-results-grid">
            {RESULTS.map(r => (
              <div key={r.metric} className="cs-result-card">
                <span className="cs-result-card__metric">{r.metric}</span>
                <p className="cs-result-card__label">{r.label}</p>
              </div>
            ))}
            {RESULT_BULLETS.map(b => (
              <div key={b} className="cs-result-card cs-result-card--bullet">
                <div className="cs-result-card__icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <p className="cs-result-card__label">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <News isAbout={true} />
    </Layout>
  </div>
)

export default EmeAquaticSystems
